import { alertController, loadingController, modalController, toastController } from '@ionic/core';
import { Component, Element, Event, EventEmitter, forceUpdate, Fragment, h, Prop, State, Watch } from '@stencil/core';
import {
  Calendar,
  CalendarDoc,
  Constants,
  DateUtils,
  FeedItemSubType,
  FeedItemType,
  FeedUtils,
  FileDoc,
  FilePageDoc,
  FileType,
  MarkupDoc,
  PersonDoc,
  PhotoAnalysis,
  PhotoDoc,
  ProjectDoc,
  RoleUtils,
  SubmittalCommonUtils,
  SubmittalDoc,
  SubmittalFeedItemDoc,
  SubmittalMarkupDoc,
  SubmittalRevision,
  TaskDoc,
  TaskRelationship,
  WorkflowState,
} from 'common';
import { CompanyDoc } from 'common/dist/types/models/Project';
import { RequestAccess } from 'src/components/concourse-login/share-link/request-access';
import { CalendarUtils } from 'src/components/concourse-schedule/utils/CalendarUtils';
import { ChangeEvent, ConcourseDb, DbListener } from 'src/global/ConcourseDb';
import { DateSelector } from 'src/global/ui/date-selector';
import { SubmittalService } from 'src/submittals/services/SubmittalService';
import { SubmittalEditModal, SubmittalUtils } from '..';
import { ConcourseMessageModal } from '../../components/common/concourse-message-modal';
import { ConcoursePersonSelector } from '../../components/concourse-person-selector/concourse-person-selector';
import { ScheduleData } from '../../components/concourse-schedule/model/ScheduleData';
import { FeedFilter, FeedService, PeopleService, peopleState, SharedUtils, WorkflowUtils } from '../../global';
import { User } from '../../global/models';
import { CommentSubmit } from '../../global/models/CommentSubmit';
import { FileService } from '../../global/service/FileService';
import { ImageService } from '../../global/service/ImageService';
import { ProjectService } from '../../projects/services/ProjectService';
import { DocumentViewerPreviewPage } from './submittal-files-viewer';
import { isUndefined } from 'lodash';

@Component({
  tag: 'concourse-submittal-detail',
  styleUrls: ['concourse-submittals.css', '../../global/styles/workflow-view.css', '../../global/app.css'],
  shadow: true,
})
export class ConcourseSubmittalDetail {
  static async present(projectId: string, submittalId: string, cssClass: string): Promise<void> {
    const modal = await modalController.create({
      component: 'concourse-submittal-detail',
      componentProps: { submittalId, projectId, isSidebarView: true, showLogo: false },
      cssClass: 'concourse-modal ' + cssClass,
      backdropDismiss: false,
    });

    return new Promise(async resolve => {
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  // -------------------------
  // PROPS, STATE & EVENTS
  // -------------------------

  @Element() el: HTMLElement;

  @Prop() projectId: string;
  @Prop() submittalId: string;
  @Prop() isModal: boolean;
  @Prop() isSidebarView: boolean;

  @State() selectedPanel: 'details' | 'preview' | 'feed' = 'feed';
  @State() submittal: SubmittalDoc;
  @State() calendar: Calendar;
  @State() isStateActionLoading: boolean = false;
  @State() revisionConfirmModalOpen: boolean = false;
  @State() files: FileDoc[];
  @State() scheduleTasks: TaskDoc[];
  @State() feed: SubmittalFeedItemDoc[];
  @State() feedLoadProgress: number = 0;
  @State() selectedRevisionIndex: number = -1; // -1 means current revision (latest)
  @State() newComment: string = '';
  @State() feedSelected: SubmittalFeedItemDoc[] = [];
  //@State() loading: boolean = false;
  @State() error: string = null;

  @State() descriptionTextExpanded: boolean = false;
  @State() activeFilter: FeedFilter = FeedFilter.ALL;
  //@State() calendar: Calendar;
  @State() scheduleData: ScheduleData;
  @State() headerSearchTerm: string = '';
  @State() pageInfo: { currentIndex: number; total: number } = { currentIndex: 0, total: 0 };
  @State() filePages: FilePageDoc[] = [];
  @State() markups: SubmittalMarkupDoc[] = [];

  @Event({ bubbles: false }) dismiss: EventEmitter;
  //@Event() closeClick?: EventEmitter<void>;

  //private permissions: PermissionsSet;
  private project: ProjectDoc;

  // -------------------------
  // PRIVATE FIELDS
  // -------------------------

  private commentsRef: HTMLDivElement;
  private submittalFilesViewerEl?: HTMLSubmittalFilesViewerElement;
  private pagesDbListeners = new Map<string, DbListener>();
  /** Pages keyed by file id — rebuilt into filePages in accepted_answers order. */
  private pagesByFileId = new Map<string, FilePageDoc[]>();
  /** Snapshot of accepted file ids in accepted_answers order for the current load. */
  private orderedAcceptedFileIds: string[] = [];
  /** Bumped on each load so stale page-db callbacks are ignored. */
  private pagesLoadGeneration = 0;

  private markupsDbListener: DbListener = {
    onComplete: (docs: SubmittalMarkupDoc[]) => this.handleSubmittalMarkups(docs),
    onChange: (event: ChangeEvent) => this.handleSubmittalMarkups(event.docs as SubmittalMarkupDoc[]),
    onError: (error: Error) => console.error('Submittal markups DB error:', error),
  };

  private submittalsDbListener: DbListener = {
    onComplete: (docs: SubmittalDoc[]) => {
      this.handleSubmittalDocs(docs);
    },
    onChange: (event: ChangeEvent) => {
      if (event.direction === 'pull') {
        this.handleSubmittalDocs(event.docs);
      }
    },
    onError: (error: Error) => {
      console.error('Submittals DB error:', error);
      this.error = error.message;
    },
  };

  private feedDbListener: DbListener = {
    onProgress: (percent: number) => {
      this.feedLoadProgress = percent;
    },
    onComplete: (docs: SubmittalFeedItemDoc[]) => {
      this.handleFeedDocs(docs);
    },
    onChange: (event: ChangeEvent) => {
      this.handleFeedDocs(event.docs as SubmittalFeedItemDoc[]);
    },
    onError: async (error: Error) => {
      console.error('Feed DB error:', error);
      if (ConcourseDb.isMissingRemoteDbError(error)) {
        const response = await SubmittalService.ensureFeedDb(this.projectId, this.submittalId);
        if (response.ok) {
          console.log('Feed DB created successfully');
        }
      }
    },
  };

  private handleFeedDocs(docs: SubmittalFeedItemDoc[]) {
    this.feed = (docs || []).filter(doc => !doc._id?.startsWith('_design')) as SubmittalFeedItemDoc[];

    // Backfill only when missing (legacy docs); never overwrite completed_state_index from feed.
    if (!this.revision?.states?.length || this.revision.completed_state_index !== undefined) return;
    const statusItems = this.feed.filter(doc => doc.sub === FeedItemSubType.STATUS).sort(FeedUtils.sortOldestFirst);
    if (statusItems.length > 0) {
      const lastStatusItem = statusItems[statusItems.length - 1];
      const index = this.revision.states.findIndex(state => state.id === lastStatusItem.sub_id);
      if (index >= 0) this.revision.completed_state_index = index;
    }
  }

  private filesDbListener: DbListener = {
    onComplete: (docs: FileDoc[]) => {
      this.files = docs;
    },
    onChange: (event: ChangeEvent) => {
      this.files = event.docs;
    },
    onError: (error: Error) => {
      console.error('Files DB error:', error);
    },
  };

  // -------------------------
  // GETTERS
  // -------------------------

  get dbName(): string {
    return `p-${this.projectId}-submittals`;
  }

  get hasDocumentWriteAccess(): boolean {
    return RoleUtils.hasDocumentWriteAccess(this.dbName, this.submittalId, this.user.roles);
  }

  get canWrite(): boolean {
    return this.currentState != null && this.hasDocumentWriteAccess;
  }

  get isAdmin(): boolean {
    return RoleUtils.hasProjectWriteRole(this.projectId, this.user.roles) || RoleUtils.hasFeatureWriteRole(this.projectId, 'submittals', this.user.roles);
  }

  get canRead(): boolean {
    return RoleUtils.hasDocumentReadAccess(this.dbName, this.submittalId, this.user.roles);
  }

  get readRole(): string {
    return `${this.dbName}-${this.submittalId}-read`; // Use RoleUtils.getDocumentReadRole()
  }

  get user(): User {
    return globalThis.user;
  }

  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  get isMobileOrSidebar(): boolean {
    return this.isMobile || this.isSidebarView;
  }

  get company(): CompanyDoc {
    return Constants.DEFAULT_COMPANY; // Fix me.
  }

  get canCreateRevision(): boolean {
    return this.selectedIsLatest && this.canWrite;
  }

  get latestRevision(): SubmittalRevision {
    return this.submittal ? SubmittalCommonUtils.getLatestRevision(this.submittal) : null;
  }

  get revision(): SubmittalRevision {
    if (this.selectedRevisionIndex === -1) {
      return this.latestRevision;
    }
    return this.submittal.revisions[this.selectedRevisionIndex];
  }

  get currentState(): WorkflowState {
    return SubmittalCommonUtils.getCurrentState(this.revision);
  }

  get nextState(): WorkflowState {
    return SubmittalCommonUtils.getNextState(this.revision);
  }

  get selectedIsLatest(): boolean {
    return this.selectedRevisionIndex === -1 || this.selectedRevisionIndex === this.submittal.revisions.length - 1;
  }

  get revisionFiles(): FileDoc[] {
    if (!this.files || !this.revisionFeed) return [];
    return SubmittalCommonUtils.filterRevisionFiles(this.files, this.revisionFeed);
  }

  get acceptedAnswers(): SubmittalFeedItemDoc[] {
    if (!this.revision || !this.revisionFeed) return [];
    return SubmittalCommonUtils.filterAcceptedAnswers(this.revision, this.revisionFeed);
  }

  get acceptedFiles(): FileDoc[] {
    if (!this.files?.length || !this.revision || !this.revisionFeed) return [];
    return SubmittalCommonUtils.getAcceptedFileDocs(this.revision, this.revisionFeed, this.files);
  }

  /** Unaccepted FILE feed items paired with their FileDoc for the viewer. */
  get unacceptedFiles(): { feedItemId: string; file: FileDoc }[] {
    if (!this.revision || !this.revisionFeed || !this.files?.length) return [];
    return SubmittalUtils.getUnacceptedFileFeedItems(this.revision, this.revisionFeed)
      .map(item => {
        const file = this.files.find(f => f._id === item.sub_id);
        return file && !file.deleted_by ? { feedItemId: item._id, file } : null;
      })
      .filter((entry): entry is { feedItemId: string; file: FileDoc } => !!entry);
  }

  /** Unaccepted PHOTO feed items paired with their FileDoc for the viewer. */
  get unacceptedPhotos(): { feedItemId: string; file: FileDoc }[] {
    if (!this.revision || !this.revisionFeed || !this.files?.length) return [];
    return SubmittalUtils.getUnacceptedPhotoFeedItems(this.revision, this.revisionFeed)
      .map(item => {
        const file = this.files.find(f => f._id === item.sub_id);
        return file && !file.deleted_by ? { feedItemId: item._id, file } : null;
      })
      .filter((entry): entry is { feedItemId: string; file: FileDoc } => !!entry);
  }

  get revisionFeed(): SubmittalFeedItemDoc[] {
    if (!this.feed || !this.revision) return null;
    return SubmittalCommonUtils.filterRevisionFeed(this.feed, this.revision.num);
  }

  get dueDate(): Date | null {
    return this.submittal.due_date ? DateUtils.fromISO(this.submittal.due_date) : null;
  }

  get svgCoverPage(): string {
    if (!this.submittal || !this.project) return '';
    return SubmittalCommonUtils.getCoverPageSvg({
      submittal: this.submittal,
      people: this.people,
      project: this.project,
      feed: this.feed,
      files: this.acceptedFiles,
      company: this.company,
      calendar: this.calendar,
    });
  }

  get feedPage(): string {
    if (!this.submittal || !this.project) return '';
    return SubmittalCommonUtils.getFeedPageSvg(this.submittal, this.people, this.project, this.company, this.acceptedAnswers, this.files);
  }

  get previewPages(): DocumentViewerPreviewPage[] {
    const initialLabels = ['Cover', 'Comments'];
    const pageWidth = SubmittalCommonUtils.PAGE_WIDTH;
    const pageHeight = SubmittalCommonUtils.PAGE_HEIGHT;
    const initialPages = [this.svgCoverPage, this.feedPage].map((svg, index) => ({
      id: `__initial_${index}`,
      label: initialLabels[index],
      svg,
      thumb_width: pageWidth,
      thumb_height: pageHeight,
    }));
    const filePages = (this.filePages || []).map((page, index) => {
      const file = this.revisionFiles?.find(f => f._id === page.file_id);
      const isFirstPageOfNewDoc = index === 0 || page.file_id !== this.filePages[index - 1].file_id;
      const separatorPageCount = file?.num_pages ?? this.filePages.filter(p => p.file_id === page.file_id).length;
      return {
        id: page._id,
        label: `${file?.name ? `${file.name} – ` : ''}Page ${page._id}`,
        thumb: page.thumb,
        thumb_width: page.thumb_width,
        thumb_height: page.thumb_height,
        showFileSeparator: isFirstPageOfNewDoc,
        separatorLabel: isFirstPageOfNewDoc ? `${file?.name ?? page.file_id} | ${separatorPageCount} ${separatorPageCount === 1 ? 'page' : 'pages'}` : undefined,
      };
    });
    return [...initialPages, ...filePages];
  }

  get previewPagesLoading(): boolean {
    if (!this.revisionFiles?.length) return false;
    if (!this.filePages.length) return true;
    return this.filePages.some(page => !page.thumb);
  }

  get people(): PersonDoc[] {
    return peopleState.people;
  }

  // -------------------------
  // LIFECYCLE
  // -------------------------

  async componentWillLoad() {
    console.log('concourse-submittal-detail componentWillLoad', this.submittalId);

    this.project = await ProjectService.getProjectDoc(this.projectId);
    if (this.canRead) {
      this.loadSubmittalData();
      this.loadFilesData();
      this.loadFeedData();
      this.loadMarkupsData();
      this.loadCalendarData();
    } else {
      RequestAccess.presentModal(this.projectId, 'Submittals', this.readRole);
    }

    this.selectedPanel = this.isMobile ? 'preview' : 'feed';
    console.log('concourse-submittal-detail componentWillLoad end', this.submittalId);
  }

  private async loadScheduleData() {
    this.scheduleData = new ScheduleData(this.projectId);
    this.scheduleData.addEventListener(ScheduleData.LOAD_COMPLETE, this.onScheduleDataLoadComplete);
    this.scheduleData.loadScheduleData();
  }

  /** Stable reference for add/removeEventListener */
  private readonly onScheduleDataLoadComplete = (_e: CustomEvent) => {
    this.scheduleTasks = this.scheduleData.tasks;
  };

  async componentDidLoad() {
    SharedUtils.scrollToBottom(this.commentsRef);
  }

  async disconnectedCallback() {
    if (this.scheduleData) {
      this.scheduleData.dispose();
      this.scheduleData.removeEventListener(ScheduleData.LOAD_COMPLETE, this.onScheduleDataLoadComplete);
      this.scheduleData = null;
    }
    this.scheduleData = null;
    const submittalsDb = SubmittalService.getSubmittalsDb(this.projectId);
    submittalsDb?.removeListeners(this.submittalsDbListener);

    const filesDb = FileService.getFilesDb(this.projectId);
    filesDb?.removeListeners(this.filesDbListener);

    const feedDb = SubmittalService.getFeedDb(this.projectId, this.submittalId);
    feedDb?.removeListeners(this.feedDbListener);

    this.cleanupMarkupsDb();
    this.cleanupFileDbs();
  }

  private async loadSubmittalData() {
    const submittalsDb = ConcourseDb.getInstance(this.dbName);
    submittalsDb.addListeners(this.submittalsDbListener);
    submittalsDb.syncRemote();
  }

  private async loadFeedData() {
    const feedDb = SubmittalService.getFeedDb(this.projectId, this.submittalId);
    feedDb.addListeners(this.feedDbListener);
    await feedDb.syncRemote();
  }

  private async loadMarkupsData() {
    const markupsDb = SubmittalService.getMarkupsDb(this.projectId, this.submittalId);
    markupsDb.addListeners(this.markupsDbListener);
    await markupsDb.syncRemote();
  }

  private handleSubmittalMarkups(docs: SubmittalMarkupDoc[]) {
    this.markups = (docs || []).filter(markup => markup && !markup['deleted_by']);
  }

  private async loadCalendarData() {
    if (this.project.default_calendar_unique_id !== undefined) {
      const calendarsDb = CalendarUtils.getCalendarsDb(this.project._id);
      calendarsDb.addListeners(this.calendarsDbListener);
      await calendarsDb.syncRemote();
    } else {
      this.calendar = Calendar.getDefaultCalendar();
    }
  }

  private async loadFilesData() {
    const filesDb = FileService.getFilesDb(this.projectId);
    filesDb.addListeners(this.filesDbListener);
    await filesDb.syncRemote();
  }

  @Watch('files')
  onFilesStateChange() {
    void this.loadRevisionFilePages();
  }

  @Watch('feed')
  onFeedStateChange() {
    void this.loadRevisionFilePages();
  }

  @Watch('selectedRevisionIndex')
  onSelectedRevisionIndexChange() {
    void this.loadRevisionFilePages();
  }

  @Watch('submittal')
  onSubmittalChange() {
    if (!this.submittal) return;
    void this.loadRevisionFilePages();
  }

  private async loadRevisionFilePages() {
    if (!this.revision || !this.revisionFeed) {
      this.filePages = [];
      this.orderedAcceptedFileIds = [];
      return;
    }

    const orderedIds = SubmittalCommonUtils.getAcceptedFileIds(this.revision, this.revisionFeed).filter(fileId => {
      const file = this.files?.find(f => f._id === fileId);
      return !!file && !file.deleted_by;
    });
    const sameFiles = orderedIds.length > 0 && orderedIds.length === this.pagesDbListeners.size && orderedIds.every(fileId => this.pagesDbListeners.has(fileId));

    // Same accepted files (possibly reordered): keep synced pages, just re-flatten in accepted_answers order.
    if (sameFiles) {
      this.orderedAcceptedFileIds = orderedIds;
      this.rebuildFilePages();
      return;
    }

    const generation = ++this.pagesLoadGeneration;
    this.cleanupPagesDb();
    this.pagesByFileId.clear();
    this.orderedAcceptedFileIds = orderedIds;

    if (!orderedIds.length) {
      this.filePages = [];
      return;
    }

    this.filePages = [];
    await Promise.all(
      orderedIds.map(fileId => {
        if (generation !== this.pagesLoadGeneration) return;
        const file = this.files?.find(f => f._id === fileId);
        if (!file || file.type == FileType.PHOTO) return;
        return this.loadFilePages(file ?? ({ _id: fileId } as FileDoc), generation);
      }),
    );
  }

  private loadFilePages(file: FileDoc, generation: number) {
    const db = FileService.getFilePagesDb(this.projectId, file._id);
    const existing = this.pagesDbListeners.get(file._id);
    if (existing) {
      db.removeListeners(existing);
      this.pagesDbListeners.delete(file._id);
    }
    const listener: DbListener = {
      onComplete: (docs: FilePageDoc[]) => this.mergeFilePages(file, docs, generation),
      onChange: (event: ChangeEvent) => this.mergeFilePages(file, event.docs as FilePageDoc[], generation),
      onError: (error: Error) => console.error('File pages DB error:', error),
    };
    this.pagesDbListeners.set(file._id, listener);
    db.addListeners(listener);
    void db.syncRemote();
  }

  private mergeFilePages(file: FileDoc, docs: FilePageDoc[], generation: number) {
    if (generation !== this.pagesLoadGeneration) return;
    const incoming = (docs || [])
      .filter(page => page && (!page.file_id || page.file_id === file._id))
      .map(page => ({ ...page, file_id: file._id }))
      .sort((a, b) => {
        const numA = Number.parseInt(a._id, 10);
        const numB = Number.parseInt(b._id, 10);
        if (Number.isFinite(numA) && Number.isFinite(numB) && numA !== numB) return numA - numB;
        return a._id.localeCompare(b._id);
      });
    if (!incoming.length) {
      this.pagesByFileId.delete(file._id);
    } else {
      this.pagesByFileId.set(file._id, incoming);
    }
    this.rebuildFilePages();
  }

  /** Flatten pagesByFileId strictly in accepted_answers order (first approved first). */
  private rebuildFilePages() {
    const pages: FilePageDoc[] = [];
    for (const fileId of this.orderedAcceptedFileIds) {
      const filePages = this.pagesByFileId.get(fileId);
      if (filePages?.length) pages.push(...filePages);
    }
    this.filePages = pages;
  }

  private onMarkupChanged = async (event: CustomEvent<MarkupDoc>) => {
    const markup = event.detail;
    if (!markup.fileId || !markup.pageId) {
      throw new Error('File ID and page ID are required');
    }
    if (!this.revision) {
      throw new Error('Revision is required to save markups');
    }
    const submittalMarkup: SubmittalMarkupDoc = {
      ...markup,
      revision: this.revision.num,
    };
    const db = SubmittalService.getMarkupsDb(this.projectId, this.submittalId);
    const saved = (await db.saveDoc(submittalMarkup)) as SubmittalMarkupDoc;
    const index = this.markups.findIndex(m => m._id === saved._id);
    if (index >= 0) {
      this.markups = this.markups.map((m, i) => (i === index ? saved : m));
    } else {
      this.markups = [...this.markups, saved];
    }
    await this.maybeAddMarkupFeedItem(markup.fileId);
  };

  /** One MARKED feed item per file / user / calendar day. */
  private async maybeAddMarkupFeedItem(fileId: string) {
    if (this.hasMarkupFeedItemToday(fileId)) return;

    const file = this.files?.find(f => f._id === fileId);
    const filename = file?.name || 'document';
    await this.addFeedItem(FeedItemSubType.MARKED, fileId, `Marked up ${filename}`);
  }

  private hasMarkupFeedItemToday(fileId: string): boolean {
    const today = DateUtils.currentIso;
    return (this.feed || []).some(
      item => item.sub === FeedItemSubType.MARKED && item.sub_id === fileId && item.user_id === this.user.id && !item.deleted_date && DateUtils.isSameDayString(item.date, today),
    );
  }

  private cleanupFileDbs() {
    this.cleanupPagesDb();
  }

  private cleanupPagesDb() {
    for (const [fileId, listener] of this.pagesDbListeners.entries()) {
      const dbInstance = ConcourseDb.instances[FileService.getFilePagesDbName(this.projectId, fileId)] as ConcourseDb | undefined;
      if (dbInstance) {
        dbInstance.removeListeners(listener);
      }
      FileService.stopFilePagesDb(this.projectId, fileId);
    }
    this.pagesDbListeners.clear();
    this.pagesByFileId.clear();
  }

  private cleanupMarkupsDb() {
    const dbName = SubmittalCommonUtils.getSubmittalMarkupsDbName(this.projectId, this.submittalId);
    const dbInstance = ConcourseDb.instances[dbName] as ConcourseDb | undefined;
    if (dbInstance) {
      dbInstance.removeListeners(this.markupsDbListener);
    }
    ConcourseDb.stopInstance(dbName);
  }

  // -------------------------
  // DATA / SAVE
  // -------------------------

  handleSubmittalDocs(docs: SubmittalDoc[]) {
    this.submittal = docs.find(doc => doc._id === this.submittalId && !doc.deleted_date);

    const canReadSchedule = RoleUtils.hasFeatureReadAccess(`p-${this.projectId}-schedule`, this.user.roles);
    if (this.submittal && canReadSchedule && this.submittal.successors?.length > 0) {
      this.loadScheduleData();
    }
  }

  private calendarsDbListener: DbListener = {
    onComplete: (docs: CalendarDoc[]) => this.handleCalendarDocs(docs),
    onChange: (event: ChangeEvent) => {
      this.handleCalendarDocs(event.docs as CalendarDoc[]);
    },
    onError: (error: Error) => console.error(error.message),
  };

  handleCalendarDocs(docs: CalendarDoc[]) {
    const defaultCalendarDoc = docs.find(doc => doc.unique_id === this.project.default_calendar_unique_id);
    this.calendar = defaultCalendarDoc ? new Calendar(defaultCalendarDoc) : CalendarUtils.getDefaultCalendar();
  }

  async saveSubmittal(): Promise<void> {
    if (!this.submittal) return;
    try {
      this.submittal = await SubmittalService.updateSubmittal(this.projectId, this.submittal);
    } catch (error) {
      console.error('Error saving submittal:', error);
      const alert = await alertController.create({
        header: 'Error',
        message: `Failed to save submittal: ${error.message}`,
        buttons: ['OK'],
      });
      await alert.present();
    }
  }

  createRevision(): void {
    if (!this.canCreateRevision) return;
    if (!this.revision.states || this.revision.states.length === 0) return;
    this.revisionConfirmModalOpen = true;
  }

  async doCreateRevision(): Promise<void> {
    this.revisionConfirmModalOpen = false;
    const prevRevision = this.revision;
    const newNum = prevRevision?.num != null ? prevRevision.num + 1 : 1;
    const newRevision: SubmittalRevision = {
      num: newNum,
      created_by: this.user.id,
      created_date: DateUtils.currentIso,
      accepted_answers: [],
      states: [...prevRevision.states],
    };
    this.submittal.revisions = [...this.submittal.revisions, newRevision];
    await this.saveSubmittal();
    await this.addFeedItem(FeedItemSubType.CREATE, String(newNum), `(Rev ${newNum})`);
  }

  selectRevision(index: number): void {
    this.selectedRevisionIndex = index;
  }

  onRemoveTaskClick(relationship: TaskRelationship): void {
    this.submittal.successors = this.submittal.successors.filter(d => d.task_unique_id !== relationship.task_unique_id);
    this.saveSubmittal();
  }

  // -------------------------
  // NAVIGATION & MODALS
  // -------------------------

  handleHeaderDismiss() {
    //this.closeClick?.emit();
    if (this.isModal || this.isSidebarView) {
      this.dismiss.emit();
    } else {
      window.location.hash = `/project/${this.projectId}/submittals`;
    }
  }

  async openEditModal() {
    if (!this.canWrite) return;
    const saved = await SubmittalEditModal.present(this.projectId, this.submittal);
    if (saved) {
      this.submittal = saved;
    }
  }

  toggleDescriptionText() {
    this.descriptionTextExpanded = !this.descriptionTextExpanded;
  }

  setFilter(filter: FeedFilter) {
    this.activeFilter = filter;
  }

  openDueDateSelector(): void {
    if (!this.canWrite) return;
    DateSelector.presentModal(this.submittal.due_date, async (date: string) => {
      if (date) {
        this.submittal.due_date = date;
        await this.saveSubmittal();
      }
    });
  }

  // -------------------------
  // WORKFLOW / STATES
  // -------------------------

  async moveToPrevState(stateId: string | null, actor: PersonDoc): Promise<void> {
    const message = await ConcourseMessageModal.present(
      `Return submittal to ${actor.first_name}`,
      'Please provide a message explaining why this submittal is being returned.',
      'Return',
    );

    if (message) {
      try {
        this.submittal = await SubmittalService.returnToState(this.projectId, this.submittalId, stateId, message);
        toastController
          .create({
            message: `The submittal has been returned to ${actor.first_name}`,
            duration: 2000,
            position: 'bottom',
          })
          .then(toast => toast.present());
      } catch (error) {
        console.error('Error returning submittal:', error);
      }
    }
  }

  async doStateAction(state: WorkflowState): Promise<void> {
    this.isStateActionLoading = true;

    try {
      const nextState = this.nextState;
      // No next state, means this will complete the submittal.
      if (!nextState && this.revision.accepted_answers.length === 0) {
        return this.showNowAnswersSelectedAlert();
      }

      const isResponsible = state.dueBy?.includes(this.user.id);
      if (state.dueBy && state.dueBy.length > 0 && !isResponsible) {
        // Users with write permissions can complete the state, even if its not assigned to them.
        if (!(await this.showNotResponsibleAlert(state))) {
          return;
        }
      }
     

      if (nextState && (!nextState.dueBy || nextState.dueBy.length === 0)) {
        const people = await ConcoursePersonSelector.present({
          projectId: this.projectId,
          feature: 'submittals',
          featureId: this.submittalId,
          peopleToOmitIds: state.dueBy,
          multiSelect: true,
          needsWrite: true,
          modalTitle: `Choose who will ${nextState.action} this submittal`,
        });
        if (people && people.length > 0) {
          nextState.dueBy = people.map(p => p._id);
          await this.completeState(state);
        }
      } else {
        await this.completeState(state);
      }
    } finally {
      this.isStateActionLoading = false;
    }
  }

  async showNotResponsibleAlert(currentState: WorkflowState): Promise<boolean> {
    const responsibleNames = currentState.dueBy
      .map(personId => {
        const person = PeopleService.findPerson(this.people, personId);
        return person ? person.first_name + ' ' + person.last_name : null;
      })
      .join(', ');

    return new Promise(async resolve => {
      const buttons = this.isAdmin
        ? [
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => resolve(false),
            },
            {
              text: 'Proceed Anyway',
              role: 'confirm',
              handler: () => resolve(true),
            },
          ]
        : [{ text: 'OK', role: 'confirm', handler: () => resolve(false) }];
      const alert = await alertController.create({
        header: 'You are not responsible for this state',
        message: `Only ${responsibleNames} may ${currentState.action} this submittal`,
        buttons: buttons,
      });
      await alert.present();
    });
  }

  async showNowAnswersSelectedAlert() {
    const alert = await alertController.create({
      header: 'No answers selected',
      message: 'Please accept one of the responses in the feed',
      buttons: ['OK'],
    });
    await alert.present();
  }

  async completeState(state: WorkflowState): Promise<void> {
    try {
      this.submittal = await SubmittalService.completeState(this.projectId, this.submittal);
      toastController
        .create({
          message: `The submittal has been ${state.pastTense}`,
          duration: 2000,
          position: 'bottom',
        })
        .then(toast => toast.present());
    } catch (error) {
      toastController
        .create({
          message: `Error completing state: ${error.message}`,
          duration: 10000,
          position: 'bottom',
        })
        .then(toast => toast.present());
      console.error('Error completing state:', error);
    }
  }

  async onRevisionChange(revision: SubmittalRevision) {
    if (!this.canWrite || !this.submittal) return;
    const prevStates = this.latestRevision?.states ?? [];
    const nextStates = revision.states ?? [];
    const wasDelete = nextStates.length < prevStates.length;
    const wasAdd = nextStates.length > prevStates.length;

    SubmittalCommonUtils.setLatestRevision(this.submittal, revision);
    forceUpdate(this);
    await this.saveSubmittal();

    toastController
      .create({
        message: wasDelete
          ? 'Submittal state deleted successfully'
          : wasAdd
            ? 'Submittal state added successfully'
            : 'Submittal state updated successfully',
        duration: 2000,
        position: 'bottom',
      })
      .then(toast => toast.present());
  }

  // -------------------------
  // COMMENTS
  // -------------------------

  async onCommentSubmit(detail: CommentSubmit) {
    const { message, files, people } = detail;
    for (const person of people) {
      await this.addDistributionPerson(person);
    }
    if (!files.length) {
      if (message) await this.addComment(message);
      else this.newComment = '';
      return;
    }
    for (const file of files) {
      await this.addFeedItem(this.getFeedSubTypeForFile(file), file._id, message || null);
      if (file.type === FileType.PHOTO) {
        const analysis: PhotoAnalysis = await ImageService.analyzePhoto(this.projectId, file.s3_key);
        await FileService.savePhoto(this.projectId, { ...(file as PhotoDoc), description: analysis.description, tags: analysis.tags });
      }
    }
    this.newComment = '';
    this.activeFilter = FeedFilter.ALL;
    SharedUtils.scrollToBottom(this.commentsRef, 200);
  }

  async onPreviewFileAdded(file: FileDoc) {
    await this.addFeedItem(this.getFeedSubTypeForFile(file), file._id, null);
    if (file.type === FileType.PHOTO) {
      const analysis: PhotoAnalysis = await ImageService.analyzePhoto(this.projectId, file.s3_key);
      await FileService.savePhoto(this.projectId, { ...(file as PhotoDoc), description: analysis.description, tags: analysis.tags });
    }
  }

  private updateRevisionAcceptedAnswers(updater: (current: string[]) => string[]): void {
    const revisionIndex = this.selectedRevisionIndex === -1 ? this.submittal.revisions.length - 1 : this.selectedRevisionIndex;
    const revision = this.submittal.revisions[revisionIndex];
    const newRevisions = [...this.submittal.revisions];
    newRevisions[revisionIndex] = { ...revision, accepted_answers: updater(revision.accepted_answers || []) };
    this.submittal = { ...this.submittal, revisions: newRevisions };
  }

  async addDistributionPerson(person: PersonDoc) {
    const distribution = this.submittal.distribution || [];
    if (!distribution.includes(person._id)) {
      distribution.push(person._id);
      this.submittal.distribution = distribution;
      await this.saveSubmittal();
    }
  }

  // -------------------------
  // FEED ITEMS
  // -------------------------

  async addFeedItem(sub: FeedItemSubType, sub_id: string, text: string = null) {
    const item: SubmittalFeedItemDoc = {
      ...FeedService.newFeedItem(this.user.id, FeedItemType.SUBMITTAL, this.submittalId, sub, sub_id, text),
      revision: this.revision.num,
    };
    try {
      await SubmittalService.storeFeedItem(this.projectId, this.submittalId, item);
      SharedUtils.scrollToBottom(this.commentsRef, 200);
    } catch (error) {
      console.error('Error adding feed item:', error);
    }
  }

  private getFeedSubTypeForFile(file: FileDoc): FeedItemSubType {
    if (file.type === FileType.PHOTO) return FeedItemSubType.PHOTO;
    if (file.type === FileType.DRAWING) return FeedItemSubType.DRAWING;
    if (file.type === FileType.SPECIFICATION) return FeedItemSubType.SPECIFICATION;
    return FeedItemSubType.FILE;
  }

  async addComment(message?: string): Promise<void> {
    const text = message ?? this.newComment;
    if (!text || !this.canWrite) return;
    await this.addFeedItem(FeedItemSubType.COMMENT, null, text);
    this.newComment = '';
    this.activeFilter = FeedFilter.ALL;
  }

  async handleFinishSubmittalMarkup(fileId: string) {
    await this.addFeedItem(FeedItemSubType.MARKED, fileId, 'marked the document');
  }

  private async downloadFile() {
    if (this.projectId && this.submittalId && this.submittal) {
      const loading = await loadingController.create({
        message: 'Generating PDF...',
      });
      try {
        await loading.present();
        await SubmittalService.exportPdf(this.projectId, this.submittalId);
      } catch (error) {
        const toast = await toastController.create({
          message: error?.message || 'Failed to export PDF',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        await loading.dismiss();
      }
      return;
    }
  }

  // -------------------------
  // RENDER
  // -------------------------

  getHeaderButtons() {
    if (!this.submittal) return null;

    const isInLatestRevision = this.latestRevision?.num === this.submittal.revisions[this.submittal.revisions.length - 1].num;
    if (this.isSidebarView || !isInLatestRevision) {
      return null;
    }
    return (
      <ion-buttons>
        {this.canWrite && (
          <ion-button data-testid="submittal-edit-button" fill="clear" shape="round" class="" onClick={() => this.openEditModal()}>
            <ion-icon slot="icon-only" name="settings-outline"></ion-icon>
          </ion-button>
        )}
        <ion-button shape="round" color="light" fill="clear" onClick={() => this.downloadFile()}>
          <ion-icon slot="icon-only" name="cloud-download-outline"></ion-icon>
        </ion-button>
      </ion-buttons>
    );
  }

  async showFullScreen() {
    ConcourseSubmittalDetail.present(this.projectId, this.submittalId, 'modal-full-view');
    if (this.isSidebarView) {
      this.dismiss.emit();
    }
  }

  private onScheduleHeaderSearch(value: string) {
    console.log('onScheduleHeaderSearch', value);
    // if (!this.scheduleData) return;
    // const v = value ?? '';
    // this.headerSearchTerm = v;
    // this.panelOpen = v.length >= 3 ? 'search' : null;
  }

  render() {
    console.log('concourse-submittal-detail render', this.submittal);
    const submittalNumber = this.submittal?.code ?? this.submittal?.['number'] ?? '';
    const submittalTitle = this.submittal ? `Submittal ${submittalNumber} Rev ${this.revision?.num}- ${this.submittal?.title}` : 'Submittal';
    return (
      <Fragment>
        {this.isSidebarView ? (
          <sidepanel-header
            number={this.submittal?.code}
            subject={this.submittal?.title}
            onShowFullscreen={() => this.showFullScreen()}
            showClose={true}
            onCloseClick={() => this.dismiss.emit()}
          />
        ) : (
          <concourse-header
            showBack={true}
            showSearchField={false}
            showSearchButton={!!this.submittal}
            searchValue={this.headerSearchTerm}
            onSearchInput={e => this.onScheduleHeaderSearch(e.detail)}
            onBack={() => this.handleHeaderDismiss()}
            projectId={this.projectId}
            pageTitle={submittalTitle}
            shareRoles={this.isMobileOrSidebar ? null : [`p-${this.projectId}-submittals-${this.submittalId}`]}
          >
            {this.getHeaderButtons()}
          </concourse-header>
        )}
        <ion-content
          scrollY={false}
          class="workflow-view__ion-content"
          style={{ '--background': this.isSidebarView ? 'var(--background-color-step-50)' : 'var(--ion-background-color)' }}
        >
          {this.submittal ? this.renderContent() : this.renderLoadingSpinner()}
        </ion-content>
        {this.revisionConfirmModalOpen && (
          <ion-modal cssClass="concourse-modal" isOpen={this.revisionConfirmModalOpen} backdropDismiss={false} onDidDismiss={() => (this.revisionConfirmModalOpen = false)}>
            <revision-confirmation-modal
              class="concourse-modal-implicit concourse-modal modal-height-auto"
              onConfirm={() => this.doCreateRevision()}
              onDismiss={() => (this.revisionConfirmModalOpen = false)}
            />
          </ion-modal>
        )}
      </Fragment>
    );
  }

  onPageInfoChange(pageInfo: { currentIndex: number; total: number }) {
    this.pageInfo = pageInfo;
  }

  private async onPreviewPageSelect(pageIndex: number) {
    this.pageInfo = { ...this.pageInfo, currentIndex: pageIndex };
    await this.submittalFilesViewerEl?.goToPageIndex(pageIndex);
  }

  private async toggleAcceptedAnswer(feedItemId: string) {
    this.updateRevisionAcceptedAnswers(ids => (ids.includes(feedItemId) ? ids.filter(id => id !== feedItemId) : [...ids, feedItemId]));
    await this.saveSubmittal();
  }

  renderLoadingSpinner() {
    return (
      <div class="ion-display-flex ion-flex-column ion-align-items-center ion-justify-content-center" style={{ height: '100%' }}>
        <ion-spinner name="crescent" slot="start" />
        <ion-label>Loading submittal...</ion-label>
      </div>
    );
  }

  renderContent() {
    return (
      <ion-grid class="ion-no-padding ion-display-flex ion-flex-column workflow-view__grid" style={{ height: '100%' }}>
        <ion-row class="workflow-view__content-container ion-flex-wrap ion-flex-md-nowrap">
          <ion-col class="ion-no-padding workflow-view__document-col ion-display-flex ion-flex-column" style={{ minWidth: '0', overflow: 'hidden' }}>
            <div class="ion-display-flex ion-flex-column workflow-view__left-column">{this.renderDocumentViewer()}</div>
          </ion-col>
          <ion-col class="ion-no-padding panel-col ion-flex-shrink-0">
            <ion-grid class="panel-layout ion-no-padding">
              <ion-row class="flex-row middle-row">
                <ion-col class="column-content">
                  <div class="panel">
                    <div class="panel-content">{this.getSidePanel()}</div>
                    {this.hasDocumentWriteAccess && <div class="panel-controls">{this.getActionButtons()}</div>}
                  </div>
                </ion-col>
                {/* right tabs on desktop */}
                <ion-col class="column-buttons ion-hide-sm-down ">{this.getTabButtons()}</ion-col>
              </ion-row>
              {/* footer buttons on mobile */}
              <ion-row class="submittal-mobile-tabs ion-hide-sm-up">{this.getMobileTabButtons()}</ion-row>
            </ion-grid>
          </ion-col>
        </ion-row>
      </ion-grid>
    );
  }

  renderDocumentViewer() {
    return (
      <submittal-files-viewer
        ref={el => (this.submittalFilesViewerEl = el as HTMLSubmittalFilesViewerElement)}
        class="submittal-document-viewer"
        project={this.project}
        calendar={this.calendar}
        submittal={this.submittal}
        revisionFeed={this.revisionFeed}
        people={this.people}
        canWrite={this.canWrite}
        fileRoles={SubmittalUtils.getReadRoles(this.projectId, this.submittalId)}
        fileType={FileType.DOCUMENT}
        files={this.acceptedFiles}
        unacceptedFiles={this.unacceptedFiles}
        unacceptedPhotos={this.unacceptedPhotos}
        externalPages={this.filePages}
        externalMarkups={this.markups}
        onRevisionChange={e => void this.onRevisionChange(e.detail)}
        onDueDateClick={() => this.openDueDateSelector()}
        onPageInfoChange={e => this.onPageInfoChange(e.detail)}
        initialPages={[this.svgCoverPage, this.feedPage]}
        onToggleAcceptedFile={e => void this.toggleAcceptedAnswer(e.detail)}
        onMarkupChanged={e => void this.onMarkupChanged(e)}
        onFileAdded={e => void this.onPreviewFileAdded(e.detail)}
      />
    );
  }

  onSegmentClick(e: Event) {
    const clickedButton = (e.target as HTMLElement).closest('ion-segment-button');
    if (!clickedButton) return;
    const value = clickedButton.value as 'details' | 'preview' | 'feed';
    if (value !== this.selectedPanel) {
      this.selectedPanel = value;
      this.newComment = '';
    }
  }

  private selectPanel(value: 'details' | 'preview' | 'feed') {
    if (value !== this.selectedPanel) {
      this.selectedPanel = value;
      this.newComment = '';
    }
  }

  getTabButtons() {
    return (
      <ion-segment value={this.selectedPanel} onClick={e => this.onSegmentClick(e)}>
        {this.getTabButton('desktop', 'feed', 'chatbox', 'Feed')}
        {this.getTabButton('desktop', 'details', 'information-circle', 'Details')}
        {this.getTabButton('desktop', 'preview', 'document', 'Preview')}
      </ion-segment>
    );
  }

  getMobileTabButtons() {
    return (
      <div class="submittal-mobile-tabs__bar" role="tablist">
        {this.getMobileTabButton('preview', 'document', 'Preview')}
        {this.getMobileTabButton('feed', 'chatbox', 'Feed')}
        {this.getMobileTabButton('details', 'information-circle', 'Details')}
      </div>
    );
  }

  private getTabButton(type: string, value: string, icon: string, label: string) {
    const isSelected = this.selectedPanel === value;
    return (
      <ion-segment-button data-testid={`infopanel-tab-button-${type}-${value}`} value={value}>
        <ion-icon name={isSelected ? icon : `${icon}-outline`}></ion-icon>
        <span class="submittal-tab-label">{label}</span>
        <span class={`submittal-tab-indicator${isSelected ? '' : ' submittal-tab-indicator--hidden'}`} aria-hidden="true"></span>
      </ion-segment-button>
    );
  }

  private getMobileTabButton(value: 'details' | 'preview' | 'feed', icon: string, label: string) {
    const isSelected = this.selectedPanel === value;
    return (
      <button
        type="button"
        role="tab"
        aria-selected={isSelected ? 'true' : 'false'}
        data-testid={`infopanel-tab-button-mobile-${value}`}
        class={{
          'submittal-mobile-tabs__button': true,
          'submittal-mobile-tabs__button--selected': isSelected,
        }}
        onClick={() => this.selectPanel(value)}
      >
        <ion-icon name={isSelected ? icon : `${icon}-outline`}></ion-icon>
        <span class="submittal-tab-label">{label}</span>
        <span class={`submittal-tab-indicator${isSelected ? '' : ' submittal-tab-indicator--hidden'}`} aria-hidden="true"></span>
      </button>
    );
  }

  getSidePanel() {
    switch (this.selectedPanel) {
      case 'details':
        return (
          <submittal-details-card
            projectId={this.projectId}
            submittal={this.submittal}
            people={this.people}
            scheduleTasks={this.scheduleTasks}
            selectedRevisionIndex={this.selectedRevisionIndex}
            descriptionTextExpanded={this.descriptionTextExpanded}
            canCreateRevision={this.canCreateRevision}
            removeTaskClick={(relationship: TaskRelationship) => this.onRemoveTaskClick(relationship)}
            createRevision={() => this.createRevision()}
            selectRevision={(index: number) => this.selectRevision(index)}
            toggleDescriptionText={() => this.toggleDescriptionText()}
          />
        );
      case 'preview':
        if (this.isMobile) {
          return this.renderDocumentViewer();
        } else {
          return (
            <submittal-preview-card
              pages={this.previewPages}
              loading={this.previewPagesLoading}
              selectedPageIndex={this.pageInfo.currentIndex}
              projectId={this.projectId}
              canWrite={this.canWrite}
              fileRoles={SubmittalUtils.getReadRoles(this.projectId, this.submittalId)}
              onSelectPage={e => void this.onPreviewPageSelect(e.detail)}
              onFileAdded={e => void this.onPreviewFileAdded(e.detail)}
            />
          );
        }
      case 'feed':
        return (
          <submittal-comments-card
            projectId={this.projectId}
            submittalId={this.submittalId}
            submittal={this.submittal}
            people={this.people}
            files={this.files}
            feed={this.revisionFeed}
            selectedRevisionIndex={this.selectedRevisionIndex}
            canWrite={this.canWrite}
            isMobile={this.isMobile}
            activeFilter={this.activeFilter}
            newComment={this.newComment}
            feedLoadProgress={this.feedLoadProgress}
            onSetFilter={e => this.setFilter(e.detail)}
            onCommentSubmit={e => void this.onCommentSubmit(e.detail)}
            onNewCommentChange={e => (this.newComment = e.detail)}
            onFeedSelectedChange={e => (this.feedSelected = e.detail)}
            onHandleFinishSubmittalMarkup={e => void this.handleFinishSubmittalMarkup(e.detail)}
            onSetCommentsRef={e => (this.commentsRef = e.detail)}
            onToggleAcceptedFile={e => void this.toggleAcceptedAnswer(e.detail)}
          />
        );

      default:
        return <div>${this.selectedPanel}</div>;
    }
  }

  getActionButtons() {
    if (!this.submittal || !this.revisionFeed || !this.people) return null;
    const currentState = this.currentState;
    const prevStatusItem = SubmittalCommonUtils.getPreviousStatusFeedItem(this.revisionFeed, this.submittal);
    const prevActor = this.people.find(p => p._id === prevStatusItem?.user_id);
    return (
      <div class="cc-padding">
        {prevStatusItem && (
          <ion-button
            data-testid="submittal-return-button"
            color="primary"
            fill="outline"
            shape="round"
            expand="block"
            disabled={this.newComment.length > 1}
            onClick={() => this.moveToPrevState(prevStatusItem.sub_id, prevActor)}
          >
            <ion-label>Return to {PeopleService.getFirstName(prevActor)}</ion-label>
          </ion-button>
        )}
        {currentState && (
          <ion-button
            data-testid="submittal-next-action"
            color="primary"
            fill="solid"
            shape="round"
            expand="block"
            onClick={() => this.doStateAction(currentState)}
            disabled={this.isStateActionLoading || this.newComment.length > 1}
          >
            {this.isStateActionLoading ? <ion-spinner name="crescent" slot="start"></ion-spinner> : null}
            {currentState.action}
          </ion-button>
        )}
      </div>
    );
  }
}
